/**
 * This is the entry point for running Hermes with USL authentication.
 * It handles a number of different authentication flows
 * For more information see: https://workivate.atlassian.net/wiki/spaces/WT/pages/5550243882/USL+Login+Flows
 */

import queryString from 'query-string';
import { FLOW } from './run-with-usl.constants';

// Shared utilities
import {
  AppLogger,
  AuthClient,
  AuthEvents,
  AuthEvent,
} from '@workivate/tho-web-shared';
import type { TokenTypes } from '@workivate/tho-web-shared';
import GoogleAnalytics from 'services/google-analytics/analytics';

// Globals
import { getRegionalApiEndpoints, lookupKeys } from 'globals/regional-api';
import { testableFeaturesEnabled, setTestableFlag } from 'globals/testable';
import waFetch from 'globals/wa-fetch';
import {
  storageKeys,
  getPersistence,
  isLocalStorageSupported,
} from 'globals/storage';

import { initiliaseWondering } from 'globals/wondering/helpers';
import { calculateResponsiveState } from 'redux-responsive';

import Language from 'language';
import {
  appendAllTestEnvironmentConfigParams,
  setClientIdToGlobalContext,
} from 'globals/fetch.helpers';
import {
  callTenancyLookup,
  initUnifiedLogin,
} from 'globals/unified-login.helpers';

// App-specific utilities
import {
  getMsTeamsUrl,
  setSubDomainCookie,
  handleSubdomainRedirect,
  getSubdomainCookie,
} from 'app/state/session/helpers';
import { runOnWebWorker } from 'app/run-on-web-worker';
import { getStore, initializeStore } from 'app/hermes-redux';

// Components
import { showToast } from 'react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import {
  getStoredSelectedEnvironment,
  setAPIEndpoints,
} from 'react-components/env-controller/env-controller-context';

// State management
import { createSubscription } from 'hermes-redux.helpers';
import rootSagas from './state/sagas';
import {
  getSubdomainFromHost,
  getDomainSuffixAndPathFromHref,
  getDomainSuffixFromHost,
} from 'router/navigation';
import * as CompanyActions from 'stores/company-store';
import isEmpty from 'lodash/isEmpty';
import { getRootHost } from 'globals/helpers';
import { renderHermes } from 'run';

import {
  logout,
  setSessionLoadingStarted,
  setSessionLoadingCompleted,
} from 'state/session/actions';
import { deleteCookie, getCookie, setCookie } from 'globals/cookieManager';
import { setDisasterRecoveryMode } from 'globals/context/disaster-recovery-context';

type AuthCookie = {
  refreshToken: string;
  clientId: string;
};

const isOnLocalhost = () => window.location.href.includes('localhost');
const currentDomain = '.' + getRootHost();
const isOnGenericSubdomain =
  getSubdomainFromHost() === 'app' || getSubdomainFromHost() === 'login';

// --------------------------------------------------------------------------------------------
// Read URL parameters

const urlParams = queryString.parse(location.search, {
  parseBooleans: true,
});

const xUslOTT: string = urlParams['x-usl-ott'];
// tenancy_hash is set by the backend, tenancyHash is set by the frontend
const tenancyHashParam: string =
  urlParams['tenancy_hash'] || urlParams['tenancyHash'];

// These parameters are only set when we are running the project locally
// due to restrictions on setting cookies through localhost
// localhost subdomains are treated as different domains and don't allow cookies to be shared
const refreshTokenParam = urlParams['refreshToken'];
const clientIdParam = urlParams['clientId'];
const userTokenParam = urlParams['userToken'];
const ottParam = urlParams['ott'];
const tokenParam = urlParams['token'];

// --------------------------------------------------------------------------------------------
// Read Storage
const stayOnGenericSubdomain =
  isOnLocalhost() &&
  localStorage.getItem(storageKeys.stayOnGenericSubdomain) === 'true';

const authCookie: AuthCookie = getCookie('USL_AUTH')
  ? JSON.parse(getCookie('USL_AUTH') || '')
  : '';

if (
  isOnGenericSubdomain &&
  isOnLocalhost() &&
  isEmpty(localStorage.getItem(storageKeys.stayOnGenericSubdomain))
) {
  localStorage.setItem(storageKeys.stayOnGenericSubdomain, 'true');
}

/**
 * Redirects after successful authentication
 * and makes sure that the credentials are set before redirecting to the company subdomain
 * This function is the USL equivalent of handleLoginRedirect in session/helpers.tsx
 * @param companySubdomain
 * @param isPostSignup
 * @param path
 * @param isOauth2
 */
export const authenticatedRedirect = (companySubdomain: string): string => {
  // Get url portion after the first dot https://app.localhost:3000/?code=56e returns .localhost:3000/?code=56e
  // https://company.test.one.telushealth.com/ -> .test.one.telushealth.com/
  const suffix = getDomainSuffixAndPathFromHref();
  // Same as above without any params or port
  const rootDomain = getDomainSuffixFromHost();
  const persistence = getPersistence();
  const msTeamsUrl = getMsTeamsUrl();
  const domain = '.' + getRootHost();

  // Extract hash fragment from current URL to preserve it during redirect
  const hashFragment = window.location.hash;

  const authData = {
    refreshToken: AuthClient.refreshToken,
    clientId: AuthClient.clientId,
    tenancyHash: localStorage.getItem(lookupKeys.tenancyHash) || '',
  };

  if (!isOnLocalhost()) {
    setCookie('USL_AUTH', JSON.stringify(authData), 5, domain);
  }

  // Extract query parameters without hash fragment
  const queryPortion = suffix.split('?')[1];
  const queryWithoutHash = queryPortion ? queryPortion.split('#')[0] : '';
  const existingParameters = new URLSearchParams(queryWithoutHash);

  // Create a new URLSearchParams object for the final query parameters
  const queryParams = new URLSearchParams();
  existingParameters.forEach((value, key) => {
    // Remove any unnecessary parameters that are not needed for the redirect
    // code and state are received from BE after we have successfully logged in to USL
    // since we are already authenticated we don't need to pass them again
    // x-usl-ott is used for SSOX and is not needed in the final redirect
    if (!EXCLUDED_REDIRECT_QUERY_PARAMS.includes(key)) {
      queryParams.append(key, value); // Add only the allowed parameters
    }
  });

  // We pass params instead of a cookie for local development with subdomains as part of the AUTH_PARAMS flow
  if (isOnLocalhost()) {
    queryParams.append('refreshToken', AuthClient.refreshToken);
    queryParams.append('clientId', AuthClient.clientId);
    queryParams.append(
      'tenancyHash',
      localStorage.getItem(lookupKeys.tenancyHash) || '',
    );
  }

  // Carry over existing test environment configuration parameters
  appendAllTestEnvironmentConfigParams({ queryParams });

  // TODO-USL: This was added 10 years ago, is this still needed?
  if (persistence) {
    queryParams.append('persistence', persistence.toString());
  }

  if (Language.getLoginLanguagePersistence()) {
    queryParams.append('lang', Language.getLocale());
  }

  if (msTeamsUrl) {
    queryParams.append('msTeamsReturnUrl', msTeamsUrl);
  }

  // Construct the clean redirect URL
  const redirectUrl = `https://${companySubdomain}${rootDomain}`;
  const cleanRedirectUrl = new URL(redirectUrl);

  // Attach the query parameters to the cleanRedirectUrl
  cleanRedirectUrl.search = queryParams.toString();

  // Preserve hash fragment by appending it to the final URL
  // Hash fragments should come after query parameters according to RFC 3986
  if (hashFragment) {
    cleanRedirectUrl.hash = hashFragment;
  }

  // Clean current data and redirect to the correct subdomain
  AuthClient.deleteAuthSession();

  window.location.href = cleanRedirectUrl.toString();
  // Returning for unit testing purposes
  return cleanRedirectUrl.toString();
};

/**
 * Make a call to /user/mobile to get the company subdomain
 */
const getCompanySubdomain = async (): Promise<string | undefined> => {
  // we need to make an auth/mobile request to get the correct company subdomain and redirect to it
  try {
    const userMobileResponse = await waFetch(
      `${getRegionalApiEndpoints().waapi_root}/user/mobile`,
      {
        method: 'get',
      },
      true,
      false,
      true,
    );

    if (userMobileResponse) {
      const { wa_subdomain } = userMobileResponse.body.company;
      if (wa_subdomain) {
        return wa_subdomain;
      } else {
        // log the user out if we can't get the company subdomain
        AppLogger.error('No subdomain found in userMobileResponse response');
        await getStore().dispatch(logout(false));
        return undefined;
      }
    }
  } catch (error) {
    AppLogger.error('Error in getCompanySubdomain', error);
    await getStore().dispatch(logout(false));
  }
};

/**
 * Logs in from an existing Auth data: clientId, refreshToken and requests a new id token
 * For development purposes we use the query params to set the auth data before we redirect via authenticatedRedirect
 */
export const setAuthFromCookieOrParams = () => {
  // Determine which tokens we should use
  // If running on localhost we need to check for query params otherwise we can use the USL_AUTH cookie
  const authCookie = JSON.parse(getCookie('USL_AUTH') as string);
  let effectiveRefreshToken: string = '',
    effectiveClientId: string = '';

  if (isOnLocalhost() && refreshTokenParam && clientIdParam) {
    effectiveClientId = clientIdParam;
    effectiveRefreshToken = refreshTokenParam;
  }

  if (authCookie?.refreshToken && authCookie?.clientId) {
    effectiveClientId = authCookie.clientId;
    effectiveRefreshToken = authCookie.refreshToken;

    // Let's delete the cookie as it's not needed anymore
    deleteCookie('USL_AUTH', currentDomain);
  }

  AuthClient.clientId = effectiveClientId;
  AuthClient.refreshToken = effectiveRefreshToken;

  AppLogger.logFeature({
    feature: 'oauth2',
    message: `setAuthFromCookieOrParams: logging in from existing AUTH data ${authCookie ? 'USL_AUTH cookie' : 'query params'}`,
    shouldLogToDatadog: true,
    messageContext: {
      clientId: effectiveClientId,
      refreshToken: effectiveRefreshToken,
    },
  });
};

export const callAuthorizeWithToken = async ({
  token,
  tokenType,
}: {
  token: string;
  tokenType: TokenTypes;
}) => {
  await AuthClient.register({ clientName: 'web', isSSOX: true });

  AppLogger.logFeature({
    feature: 'oauth2',
    message: 'Authorizing with OTT',
    shouldLogToDatadog: true,
    messageContext: { token, tokenType },
  });

  const authResponse = await AuthClient.authorizeWithToken({
    token,
    tokenType,
  });

  if (authResponse) {
    const { tenancy_hash } = authResponse;
    if (tenancy_hash) {
      localStorage.setItem(
        lookupKeys.tenancyHash,
        JSON.stringify(tenancy_hash),
      );
    }
  }
};

/**
 * Formats the tenancyHash from the URL
 */
export const parseTenancyHash = (tenancyHash: string) => {
  // Attempt to parse the tenancyHash and check that it's an array of strings
  try {
    const tenancyHashArray: string[] = JSON.parse(
      tenancyHash.replace(/'/g, '"'),
    );
    if (
      !Array.isArray(tenancyHashArray) ||
      !tenancyHashArray.every(element => typeof element === 'string')
    ) {
      throw new Error('tenancyHash must be an array of strings');
    }
    const stringifiedTenancyHash = JSON.stringify(tenancyHashArray);
    return stringifiedTenancyHash;
  } catch (error) {
    AppLogger.error('Failed to parse (incorrect format) tenancyHash:', error);
    throw error;
  }
};
const addListenerForViewportBreakpointsUpdateTrigger = store => {
  let previousFontSize = '16px';

  const viewportCheck = () => {
    const htmlElement = document.getElementsByTagName('html')[0] as HTMLElement;
    const computedFontSize = window
      .getComputedStyle(htmlElement, null)
      .getPropertyValue('font-size');

    if (computedFontSize !== previousFontSize) {
      store.dispatch(calculateResponsiveState(window));
      previousFontSize = computedFontSize;
    }
  };

  setInterval(viewportCheck, 1500);
};

export async function initPreLoginCompany() {
  // app and login subdomain will never have company details
  // so we don't need to make a request.
  if (getSubdomainFromHost() === 'app' || getSubdomainFromHost() === 'login') {
    return CompanyActions.setCompany({});
  }
  try {
    await CompanyActions.setPreLoginCompanyDetails();
  } catch {
    CompanyActions.setCompany({});
    getStore().dispatch(
      showToast({
        type: ToastType.ERROR,
        message: polyglot.t('api_errors.something_wrong'),
      }),
    );
  }
}

/**
 * This is the entry point for the Hermes application when Unified Login is enabled.
 * It is responsible for initialising the app, setting up the Redux store,
 * and rendering the main component.
 * @returns {Promise<void>}
 */
export async function initHermesWithUsl(): Promise<void> {
  initiliaseWondering();

  if (!isLocalStorageSupported()) {
    (window as Window).location = '/private-browsing';
    return;
  }

  if (window.WAM.ENV.maintenance) {
    (window as Window).location = '/maintenance';
    return;
  }

  // Allow testable flag to be disabled by query string.
  const {
    enableTestableFeatures = testableFeaturesEnabled(),
    setEnvironment: setEnvFromQueryString,
  } = queryString.parse(location.search);

  setTestableFlag(JSON.parse(enableTestableFeatures as string));

  const store = initializeStore();
  store.runSaga(rootSagas);

  // before we try to log in with a token we need to be sure it's the right environment
  const setEnvironment =
    setEnvFromQueryString ?? getStoredSelectedEnvironment() ?? null;

  if (setEnvironment && (!IS_PRODUCTION || !IS_TEST)) {
    setAPIEndpoints(setEnvironment);
  }

  // Register state listeners
  createSubscription(
    [
      'session.user.anon_user_id',
      'session.user.birthday_date',
      'session.user.gender',
      'session.user.account_type',
      'session.user.originator',
      'session.user.role',
    ],
    GoogleAnalytics.setUser,
  )(store);

  createSubscription(
    ['company.company_id', 'company.name', 'company.demo', 'company.internal'],
    GoogleAnalytics.setCompanyProperties,
  )(store);

  localStorage.setItem(storageKeys.authenticationMethod, 'oauth2');

  await Language.set();
  runOnWebWorker(initPreLoginCompany);
  Language.loadTranslations(Language.getLocale(), true);

  //set interval check for updating viewport break points on text-only zoom
  addListenerForViewportBreakpointsUpdateTrigger(store);
  renderHermes();
}

/**
 * This function redirects the user to the company subdomain if they are authenticated
 * And initialises the login process (initUnifiedLogin) by performing further api calls
 */
const handleAuthenticatedFlow = async (
  performRedirectionCheck: boolean,
): Promise<string> => {
  await callTenancyLookup();

  // In certain cases we don't even need to check if a redirect is needed
  // For example when we are on localhost and can stay on the generic subdomain
  // Or The USL_AUTH and AUTH_PARAMS flow
  // in these flows we already set some credentials after logging in from a generic subdomain and the redirect is already underway
  if (!performRedirectionCheck) {
    await initUnifiedLogin();
    return FLOW.AUTHENTICATED_SHOULD_NOT_REDIRECT;
  }

  const companySubdomain = await getCompanySubdomain();
  const subdomain = getSubdomainFromHost();

  if (companySubdomain) {
    setSubDomainCookie(companySubdomain);
  }

  if (
    companySubdomain?.toLowerCase() &&
    companySubdomain?.toLowerCase() !== subdomain?.toLowerCase()
  ) {
    const redirectMessage = `Different domain detected (${subdomain})- Redirecting to ${companySubdomain}`;
    if (isOnLocalhost() && !window.confirm(redirectMessage)) {
      // If we are on localhost and the user doesn't want to redirect, we just check if we are authenticated
      await initUnifiedLogin();
    }
    if (companySubdomain) {
      authenticatedRedirect(companySubdomain);
      return `${FLOW.AUTHENTICATED_REDIRECT_NEEDED} - ${redirectMessage}`;
    } else {
      AppLogger.error('Cannot redirect: companySubdomain is undefined');
    }
  }

  await initUnifiedLogin();

  return `${FLOW.AUTHENTICATED_NO_REDIRECT_NEEDED} - ${subdomain} is the correct subdomain`;
};

const handleUnauthenticatedFlow = async (): Promise<string> => {
  await getStore().dispatch(setSessionLoadingCompleted());
  return FLOW.NOT_AUTHENTICATED;
};

/**
 * If logged in, checks if on the correct subdomain and redirects if not
 * If not logged in, calls initialiseHermes to set up the app
 * @param attemptRedirect For local development we make redirection to a subdomain optional
 * @returns {Promise<void>}
 *
 */
export const performAuthCheck = async ({
  performRedirectionCheck = !stayOnGenericSubdomain,
  forceRefreshIdToken = false,
} = {}): Promise<void> => {
  let flowMessage: string | null = '';
  try {
    const isAuthenticated =
      await AuthClient.checkIfUserIsAuthenticated(forceRefreshIdToken);

    if (stayOnGenericSubdomain) {
      performRedirectionCheck = false;
    }

    flowMessage = isAuthenticated
      ? await handleAuthenticatedFlow(performRedirectionCheck)
      : await handleUnauthenticatedFlow();

    logFlowMessage(FLOW.AUTH_CHECK, `${flowMessage}`);
  } catch (error) {
    await getStore().dispatch(setSessionLoadingCompleted());
    AppLogger.error('Error in checkIfUserIsAuthenticated', error);
  }
};

/**
 * Determines the corect login flow based on the presence of various parameters
 */
export const determineLoginFlow = ({
  refreshTokenParamExists = false,
  wamSubdomainExists = false,
  userTokenParam = false,
  ottParam = false,
  authCookie = false,
  xUslOTT = false,
  token = false,
}: {
  refreshTokenParamExists?: boolean;
  wamSubdomainExists?: boolean;
  userTokenParam?: boolean;
  ottParam?: boolean;
  authCookie?: boolean;
  xUslOTT?: boolean;
  token?: boolean;
}): string => {
  if (wamSubdomainExists) {
    return FLOW.WAM_SUBDOMAIN;
  } else if (xUslOTT) {
    return FLOW.X_USL_OTT;
  } else if (userTokenParam && ottParam) {
    return FLOW.USERTOKEN_AND_OTT;
  } else if (userTokenParam && !ottParam) {
    return FLOW.USERTOKEN;
  } else if (token) {
    return FLOW.TOKEN;
  } else if (authCookie) {
    return FLOW.USL_AUTH;
  } else if (refreshTokenParamExists) {
    return FLOW.AUTH_PARAMS;
  }

  return FLOW.AUTH_CHECK;
};

/**
 * Logs a message to the app logger and Datadog
 * @param flow
 * @param message
 */
const logFlowMessage = (flow: string, message: string) => {
  let messageToSend = `FE Flow is: ${flow} `;
  messageToSend += `- ${message}`;
  AppLogger.logFeature({
    feature: 'oauth2',
    message: messageToSend,
    shouldLogToDatadog: true,
  });
};

/**
 * Sets the client id to the DD global context
 */
const setClientIdContext = () => {
  let effectiveClientID: string = '';
  if (clientIdParam) {
    effectiveClientID = clientIdParam;
  } else if (authCookie?.clientId) {
    effectiveClientID = authCookie.clientId;
  } else if (AuthClient.clientId) {
    effectiveClientID = AuthClient.clientId;
  }
  setClientIdToGlobalContext(effectiveClientID || '');
};

/**
 * Determines which USL login flow to use
 * Each case will eventually call performAuthCheck and initialiseHermes along with initialiseLogin if we have existing tokens
 * performAuthCheck checks if we are authenticated and redirect if needed
 * initialiseHermes sets up the app (Redux, language, etc)
 * initialiseLogin makes api calls to get and set the user and company data
 *
 * All flows except the AUTH_CHECK are cases where there is pre-existing data, we either have otts or we come from a previous flow
 *
 * case 1:WAM_SUBDOMAIN: Handle existing WAM_SUBDOMAIN cookie and redirect to the the stored subdomain if it exists
 * case 2:X_USL_OTT: We have a OTT. This is the urlParams['x-usl-ott'] SSOX approach, currently only used from Zeus to Hermes
 * case 3:USERTOKEN: There is a userToken in the urlParams, but no ott meaning that we are dealing with a deeplink
 * case 4:USERTOKEN_AND_OTT: We have a userToken and ott in the urlParams (used by natives to authenticate webviews, this is also the old ghost login flow)
 * case 5:TOKEN: There is a token in the urlParams, but no ott meaning that we are dealing with IDP initiated SSO, under the hood we will use the SSOX approach similar to the X_USL_OTT flow and USERTOKEN_AND_OTT flow
 * case 6:USL_AUTH: We have previously logged in and there is a USL_AUTH, or we are on localhost and have the query params: clientId, idToken and refreshToken
 * case 7:AUTH_CHECK: calls performAuthCheck - This is the default case if we don't have any of the above
 *       Further subflows that are handled in performAuthCheck:
 *       case AUTHENTICATED_SHOULD_NOT_REDIRECT | Authenticated and don't need to check for redirection
 *       case AUTHENTICATED_REDIRECT_NEEDED     | Authenticated and on a different subdomain needing to redirect
 *       case AUTHENTICATED_NO_REDIRECT_NEEDED  | Authenticated and on the correct subdomain
 *       case NOT_AUTHENTICATED                 | Not Authenticated token and can display the login button
 *
 * @returns {Promise<void>}
 */
export const runHermesWithUSL = async (): Promise<void> => {
  // Sets up the redux store
  await initHermesWithUsl();
  const store = getStore();
  store.dispatch(setSessionLoadingStarted());

  // Sets the client id to the DD global context
  setClientIdContext();

  AppLogger.logFeature({
    feature: 'oauth2',
    message: 'running Hermes with USL',
  });

  AuthEvents.subscribe('*', async ({ event, data, eventType }: AuthEvent) => {
    AppLogger.logFeature({
      feature: 'oauth2',
      message: `Received event ${event}`,
      messageContext: data,
    });

    if (eventType === 'error' && data?.status === 500) {
      setDisasterRecoveryMode(true);
    }
  });
  if (tenancyHashParam) {
    const parsedTenancyHash = parseTenancyHash(tenancyHashParam);
    localStorage.setItem(lookupKeys.tenancyHash, parsedTenancyHash);
  }
  let refreshTokenParamExists = false;

  if (
    !stayOnGenericSubdomain &&
    refreshTokenParam &&
    clientIdParam &&
    isOnLocalhost()
  ) {
    refreshTokenParamExists = true;
  }

  const wamSubdomainExists = !!(
    isOnGenericSubdomain &&
    getSubdomainCookie() &&
    !stayOnGenericSubdomain
  );
  const flow = determineLoginFlow({
    wamSubdomainExists,
    xUslOTT: !!xUslOTT,
    userTokenParam: !!userTokenParam,
    ottParam: !!ottParam,
    authCookie: !!authCookie,
    refreshTokenParamExists,
    token: !!tokenParam,
  });

  switch (flow) {
    case FLOW.WAM_SUBDOMAIN:
      // We have a WAM_SUBDOMAIN cookie from a previous login
      if (isOnLocalhost()) {
        // Display a confirmation dialog for localhost
        if (window.confirm('Company subdomain cookie present- Redirect Now?')) {
          await handleSubdomainRedirect();
        } else {
          // otherwise just check if we are authenticated
          await performAuthCheck({ performRedirectionCheck: false });
        }
      } else {
        // Redirect without confirmation dialog
        logFlowMessage(flow, `logging in from WAM_SUBDOMAIN cookie`);
        await handleSubdomainRedirect();
      }
      break;

    case FLOW.USL_AUTH:
      // An USL_AUTH Cookie is present from a previous login
      // The USL_AUTH cookie is set after authentication on the generic subdomains and just before redirecting to the company subdomain
      logFlowMessage(flow, `logging in from ${FLOW.USL_AUTH} cookie `);
      setAuthFromCookieOrParams();
      await performAuthCheck({
        // Since the USL_AUTH is implicitly set before a redirect we don't need to check for redirection again
        performRedirectionCheck: false,
        forceRefreshIdToken: true,
      });
      break;

    case FLOW.X_USL_OTT:
      // Oauth2 Version of Ghost Login - we exchange the OTT passed for fully qualified tokens

      logFlowMessage(flow, `x-usl-ott: ${xUslOTT} logging in with OTT`);

      await callAuthorizeWithToken({ token: xUslOTT, tokenType: 'x-usl-ott' });
      await performAuthCheck();
      break;

    case FLOW.USERTOKEN_AND_OTT:
      logFlowMessage(
        flow,
        `userToken: ${userTokenParam} ott: ${ottParam} logging in with OTT`,
      );

      await callAuthorizeWithToken({
        token: userTokenParam,
        tokenType: 'x-usl-ott',
      });
      await performAuthCheck();
      break;

    case FLOW.USERTOKEN:
      logFlowMessage(
        flow,
        `userToken: ${userTokenParam} logging in with Deeplink token`,
      );
      await callAuthorizeWithToken({
        token: userTokenParam,
        tokenType: 'x-usl-deeplink',
      });
      await performAuthCheck();
      break;

    case FLOW.TOKEN:
      // IDP initiated SSO - we exchange the token passed for fully qualified tokens
      logFlowMessage(
        flow,
        `token: ${tokenParam}, logging in with IDP initiated SSO token`,
      );
      await callAuthorizeWithToken({
        token: tokenParam,
        tokenType: 'x-usl-ott',
      });
      await performAuthCheck();
      break;
    case FLOW.AUTH_PARAMS:
      // We are on localhost and have the query params: refreshToken, clientId, tenancyHash from a previous login
      // from an incorrect subdomain (these params are only present after a redirect)
      logFlowMessage(
        flow,
        `logging in locally with auth parameters refreshToken, clientId, tenancyHash`,
      );

      setAuthFromCookieOrParams();
      // This flow happens after a redirect has taken place meaning we don't need to check if another redirect is needed
      await performAuthCheck({
        performRedirectionCheck: false,
        forceRefreshIdToken: true,
      });
      break;

    case FLOW.AUTH_CHECK:
    default: {
      await performAuthCheck();
      break;
    }
  }
};
/**
 * List of query parameters that should be excluded from the redirect URL
 * These parameters are used for authentication and should not be passed in the final redirect
 */
export const EXCLUDED_REDIRECT_QUERY_PARAMS = [
  'code',
  'state',
  'x-usl-ott',
  'userToken',
  'ott',
];
